import AppContext from 'contexts/AppContext';
import React, { useContext } from 'react';
import { Image } from 'react-feather';
import { isDateInContest } from 'utils/photo-content-helpers';

import Button from '../../buttons/Button';
import GalleryImage from './GalleryImage';
import { PhotoContestBadge } from './PhotoContestBadge';
import { DisplayImage } from 'components/poi/PoiMediaGallery';

function MultipleImages({
    images,
    handlePhotoClick
}: {
    images: DisplayImage[];
    handlePhotoClick: (index: number) => void;
}) {
    // We have three or more images. We display first 4 images.
    // Combinations of vertical and horizontal images.
    // Possible combinations:
    /**
     * - 0 vertical 4 horizontal
     *
     * - 1 vertical 3 horizontal
     * - 2 vertical 2 horizontal
     *
     * - 3 vertical 1 horizontal
     * - 4 vertical 0 horizontal
     */

    const imagesToDisplay = images.slice(0, 4);
    const verticalImages = imagesToDisplay.filter((i) => i.original.height > i.original.width);
    const horizontalImages = imagesToDisplay.filter((i) => i.original.height <= i.original.width);

    let layout = 'horizontal';
    if (verticalImages.length === 1 || verticalImages.length === 2) layout = 'mixed';
    else if (verticalImages.length > 2) layout = 'vertical';

    const { state } = useContext(AppContext);
    const { photoContest } = state;
    const photoContestActive = photoContest.state === 'ACTIVE';

    return (
        <div className="grid grid-cols-6 grid-rows-2 gap-2 h-full">
            {layout === 'horizontal' && (
                <>
                    {horizontalImages.map((image, i) => (
                        <div className="col-span-3 relative" key={i}>
                            {isDateInContest(image.created_at) && photoContestActive && <PhotoContestBadge />}
                            <GalleryImage
                                src={image.medium.url}
                                alt={image.alt}
                                handleClick={() => handlePhotoClick(image.index)}
                            />
                        </div>
                    ))}
                    {horizontalImages.length === 3 && (
                        <div className="col-span-3 relative">
                            <GalleryImage src="/images/poi-card-fallback-image.jpg" alt="Kam za vikend" />
                        </div>
                    )}
                </>
            )}
            {layout === 'mixed' && (
                <>
                    <div className="hidden lg:block col-span-3 row-span-2 relative">
                        {isDateInContest(verticalImages[0]?.created_at) && photoContestActive && <PhotoContestBadge />}
                        <GalleryImage
                            src={verticalImages[0].large.url}
                            alt={verticalImages[0].alt}
                            handleClick={() => handlePhotoClick(verticalImages[0].index)}
                        />
                    </div>
                    <div className="col-span-6 lg:col-span-3 relative">
                        {isDateInContest(horizontalImages[0]?.created_at) && photoContestActive && (
                            <PhotoContestBadge />
                        )}
                        <GalleryImage
                            src={horizontalImages[0].medium.url}
                            alt={horizontalImages[0].alt}
                            handleClick={() => handlePhotoClick(horizontalImages[0].index)}
                        />
                    </div>
                    <div className="col-span-6 lg:col-span-3 relative">
                        {horizontalImages[1] ? (
                            <>
                                {isDateInContest(horizontalImages[1]?.created_at) && photoContestActive && (
                                    <PhotoContestBadge />
                                )}
                                <GalleryImage
                                    src={horizontalImages[1].medium.url}
                                    alt={horizontalImages[1].alt}
                                    handleClick={() => handlePhotoClick(horizontalImages[1].index)}
                                />
                            </>
                        ) : (
                            <GalleryImage src="/images/poi-card-fallback-image.jpg" alt="Kam za vikend" />
                        )}
                    </div>
                </>
            )}
            {layout === 'vertical' && (
                <>
                    <div className="col-span-3 lg:col-span-2 row-span-2 relative">
                        {isDateInContest(verticalImages[0]?.created_at) && photoContestActive && <PhotoContestBadge />}
                        <GalleryImage
                            src={verticalImages[0].large.url}
                            alt={verticalImages[0].alt}
                            handleClick={() => handlePhotoClick(verticalImages[0].index)}
                        />
                    </div>
                    <div className="col-span-3 lg:col-span-2 row-span-2 relative">
                        {isDateInContest(verticalImages[1]?.created_at) && photoContestActive && <PhotoContestBadge />}
                        <GalleryImage
                            src={verticalImages[1].large.url}
                            alt={verticalImages[1].alt}
                            handleClick={() => handlePhotoClick(verticalImages[1].index)}
                        />
                    </div>
                    <div className="col-span-2 row-span-2 hidden lg:block relative">
                        {isDateInContest(verticalImages[2]?.created_at) && photoContestActive && <PhotoContestBadge />}
                        <GalleryImage
                            src={verticalImages[2].large.url}
                            alt={verticalImages[2].alt}
                            handleClick={() => handlePhotoClick(verticalImages[2].index)}
                        />
                    </div>
                </>
            )}
            <div className="absolute bottom-2 right-2">
                <Button onClick={handlePhotoClick} startIcon={<Image />} color="white">
                    Odpri galerijo
                </Button>
            </div>
        </div>
    );
}

export default MultipleImages;
